/**
 * ⟦C57 · lot 5d · A-174⟧ **Les textes du Fil, en deux longueurs.**
 *
 * Matt, 02/10/2026 : les 89 commentaires du Fil sont de la FICTION, ils restent tels quels (leur
 * texte, leurs pseudos, les répliques de vanne : `content/comments.json` n'est pas touché). Ce qui
 * passe derrière « Les dessous », ce sont les préambules de l'écran et les explications des
 * réponses : ce module porte donc, pour chaque réponse, sa promesse LONGUE (celle d'avant le
 * chantier, gardée mot pour mot : elle sert aux dessous et à l'infobulle) et sa promesse COURTE
 * (celle qui s'affiche sous le commentaire : des chiffres, une ligne).
 *
 * Pur : pas de DOM, pas de store. `fil-court.test.ts` mesure les textes courts avec le mètre de
 * l'écran (`game/dessous.ts`) sur toutes les tonalités.
 */

import {
  FID_COEUR,
  HYPE_COEUR,
  HYPE_EPINGLE_NEGATIVE,
  HYPE_EPINGLE_POSITIVE,
  HYPE_HUMOUR_RATE,
  HYPE_HUMOUR_REUSSI,
  type ReponseFil,
} from "@maxime/shared";

/** La tonalité d'un commentaire, telle que le moteur la porte (`CommentaireEnFil.tonalite`). */
export type TonaliteFil = string;

const estDur = (tonalite: TonaliteFil) => tonalite === "troll" || tonalite === "toxique";
const estDoux = (tonalite: TonaliteFil) => tonalite === "sympa" || tonalite === "star";

/** Ce que le moteur a dit d'une réponse sans jet, une fois envoyée (version d'avant le chantier). */
export const RENDU_LONG: Record<Exclude<ReponseFil, "humour">, string> = {
  coeur: "❤ Cœur envoyé.",
  epingler: "📌 Épinglé en haut de la page.",
  ignorer: "🤷 Ignoré.",
  supprimer: "🗑 Supprimé.",
};

/** La même chose, en un mot de plus que l'icône. */
export const RENDU_COURT: Record<Exclude<ReponseFil, "humour">, string> = {
  coeur: "❤ Cœur envoyé.",
  epingler: "📌 Épinglé.",
  ignorer: "🤷 Ignoré.",
  supprimer: "🗑 Supprimé.",
};

/** Le verdict d'une vanne, version d'avant le chantier. */
export function verdictHumourLong(reussi: boolean): string {
  return reussi ? "😄 La vanne passe." : "😬 Tu t'es moqué d'un fan. Ça ne passe pas.";
}

/** Le verdict d'une vanne, en une phrase courte. La réplique écrite pour le commentaire suit, intacte. */
export function verdictHumourCourt(reussi: boolean): string {
  return reussi ? "😄 La vanne passe." : "😬 La vanne ne passe pas.";
}

/**
 * Ce qu'une réponse promet, en toutes lettres (la version d'avant le chantier, inchangée). Les
 * chiffres viennent du moteur, jamais d'ici. Sert à l'infobulle du bouton et aux dessous.
 */
export function promesse(reponse: ReponseFil, tonalite: TonaliteFil, pHumour: number): string {
  const dur = estDur(tonalite);
  switch (reponse) {
    case "coeur":
      return `+${FID_COEUR.toString().replace(".", ",")} fidélité, +${HYPE_COEUR} hype.${
        tonalite === "toxique" ? " Répondre gentiment à ça coûte double." : ""
      }`;
    case "humour":
      return `${Math.round(pHumour * 100)} % que ça passe : +${HYPE_HUMOUR_REUSSI} hype et un cran vers le chaos assumé. Sinon, tu t'es moqué d'un fan (${HYPE_HUMOUR_RATE} hype).`;
    case "epingler":
      return dur
        ? `Tu mets ça en avant. ${HYPE_EPINGLE_NEGATIVE * 2} hype — amplifier un troll, c'est encore le servir.`
        : estDoux(tonalite)
          ? `+${HYPE_EPINGLE_POSITIVE * 2} hype : tout le monde le verra en premier.`
          : "Tu le mets en haut de la page. Geste éditorial, rien de plus.";
    case "ignorer":
      return "Rien. Il quitte le Fil, et c'est tout.";
    case "supprimer":
      return dur
        ? "Il disparaît, et il ne t'aura rien coûté."
        : "Il disparaît. Supprimer une critique légitime, ça compte double dans ce qui se remarque.";
  }
}

/** Ce qu'une réponse promet, en une ligne de chiffres : celle qui s'affiche sous le commentaire. */
export function promesseCourte(reponse: ReponseFil, tonalite: TonaliteFil, pHumour: number): string {
  switch (reponse) {
    case "coeur":
      return `+${FID_COEUR.toString().replace(".", ",")} fidélité · +${HYPE_COEUR} hype${
        tonalite === "toxique" ? " · coûte double" : ""
      }`;
    case "humour":
      return `${Math.round(pHumour * 100)} % : +${HYPE_HUMOUR_REUSSI} hype, sinon ${HYPE_HUMOUR_RATE} hype`;
    case "epingler":
      return estDur(tonalite)
        ? `${HYPE_EPINGLE_NEGATIVE * 2} hype : amplifier un troll, c'est le servir`
        : estDoux(tonalite)
          ? `+${HYPE_EPINGLE_POSITIVE * 2} hype : tout le monde le verra en premier`
          : "En haut de la page, rien de plus";
    case "ignorer":
      return "Rien : il quitte le Fil";
    case "supprimer":
      return estDur(tonalite)
        ? "Il disparaît, et ça ne coûte rien"
        : "Il disparaît : une critique supprimée se remarque double";
  }
}

/** Le seul mot de la hype à l'écran : où en est la communauté. Le reste de l'ancienne phrase est dessous. */
export function etatHypeCourt(hype: number): string {
  if (hype >= 70) return "ta communauté est chaude.";
  if (hype > 0) return "elle suit.";
  return "pas encore de communauté.";
}

/** L'ancienne phrase de la hype, entière : elle est dans les dessous. */
export function etatHypeLong(hype: number): string {
  if (hype >= 70) return "ta communauté est chaude — elle partagera ta prochaine vidéo.";
  if (hype > 0) return "elle suit. Elle refroidit toute seule si tu disparais.";
  return "tu n'as pas encore de communauté. Ça se construit ici.";
}

/** Le Fil vide : une phrase. L'explication (« c'est normal ») est dans les dessous. */
export function filVideCourt(sousCinquanteAbonnes: boolean): string {
  return sousCinquanteAbonnes ? "Personne n'a encore commenté." : "Ton Fil est vide.";
}

/** Le Fil vide, en toutes lettres (version d'avant le chantier). */
export function filVideLong(sousCinquanteAbonnes: boolean): string {
  return sousCinquanteAbonnes
    ? "Personne n'a encore commenté. C'est normal : à ce stade, une chaîne n'a pas de section commentaires. Continue de publier."
    : "Ton Fil est vide. Publie, ou dors — ils arrivent aux deux moments.";
}

/** Le pied d'envoi : la facture, en chiffres. */
export function ligneFactureCourte(o: {
  reponses: number;
  lot: number;
  mental: number;
  energie: number;
  heures: number;
}): string {
  const mental = o.mental === 0 ? "0" : Math.abs(o.mental).toString().replace(".", ",");
  return `${o.reponses} / ${o.lot} réponse${o.reponses > 1 ? "s" : ""} · ${mental} ❤ · ${Math.abs(o.energie)} ⚡ · ${o.heures} h`;
}

/** Le bonus d'un gros créateur dans le lot : sa propre ligne, courte. */
export function ligneStarCourte(hypeStar: number): string {
  return `⭐ Un gros créateur te répond (+${hypeStar} hype).`;
}

/** « Lire sans répondre » : le texte d'avant, rangé dans les dessous. */
export const LIRE_SANS_REPONDRE_LONG =
  "Lire sans répondre reste possible — ça ne coûte pas plus cher, et tu gardes ton idée.";

/** Le community manager, en une ligne par état. Le détail est dans les dessous du bloc. */
export const CM_ATTEND_COURT = "Il attend le Grand Saut.";
export const CM_ARRETE_COURT = "Arrêté : plus un Click ne part.";

export function cmEnPosteCourt(o: { traitement: number; coutHebdo: number }): string {
  return `En poste : ${o.traitement} cœurs par nuit · ${o.coutHebdo} ₵ / semaine.`;
}

/** Ce que le CM prendrait ce soir : les pseudos, ou rien. */
export function cmCeSoirCourt(auteurs: readonly string[]): string {
  return auteurs.length === 0
    ? "Ce soir : rien de routine à traiter."
    : `Ce soir : ${auteurs.map((a) => `« ${a} »`).join(", ")}.`;
}
